import React from 'react'
import { toast } from 'react-toastify'
import { Box, Grid, MenuItem, Select, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useMutation } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import RewardsAPI from 'api/faber/RewardsAPI'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

import NormalText from '../../../../common/Typography/NormalText'

const CustomDatePicker = (props: DatePickerProps) => {
  const { label, disabled, date, setDate } = props

  const handleStartDate = (newValue: Date | null) => {
    setDate(newValue)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DesktopDatePicker
        label={label}
        inputFormat='dd/MM/yyyy'
        value={date}
        onChange={handleStartDate}
        renderInput={(params) => (
          <TextField placeholder='Date' variant='standard' {...params} />
        )}
        disabled={disabled}
      />
    </LocalizationProvider>
  )
}

export default CustomDatePicker

interface DatePickerProps {
  label: string
  disabled: boolean
  date: Date | null
  setDate: (value: Date | null) => void
}

const formikInitialValues = {
  campaignRewardName: '',
  campaignSummary: '',
  campaignCode: '',
  campaignCodePrefix: '',
  rewardProductId: '',
  maxClaims: 0,
  maxConsumption: 0,
  validityStartAt: 0,
  validityEndAt: 0
}

const NewCampaignPromoSchema = Yup.object().shape({
  campaignRewardName: Yup.string().required('Campaign promo name is required'),
  campaignSummary: Yup.string().required('Campaign promo summary is required'),
  campaignCode: Yup.string().required('Campaign promo code is required'),
  rewardProductId: Yup.number()
    .min(0, 'Reward product ID cannot be negative')
    .required('Reward product ID is required'),
  maxClaims: Yup.number()
    .min(0, 'Input cannot be negative')
    .required('Max claims amount is required'),
  maxConsumption: Yup.number()
    .min(0, 'Input cannot be negative')
    .required('Max consumption amount is required')
})

const CenteredBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '80%'
})
const CenteredGrid = styled(Grid)({
  display: 'flex',
  alignItems: 'center'
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

export const NewCampaignPromoForm = ({
  handleClose
}: {
  handleClose: () => void
}) => {
  const [validityStartAt, setValidityStart] = React.useState<Date | null>(
    new Date()
  )
  const [validityEndAt, setValidityEnd] = React.useState<Date | null>(new Date())

  const faberToken = OrbitSession.get('faberToken')
  const { data: rewardProducts } = useQuery(['getRewardProducts'], async () =>
    RewardsAPI.getRewardProducts(faberToken)
  )

  const { mutate: createPromoCode } = useMutation(RewardsAPI.createPromoCode, {
    onSuccess: () => {
      toast.success('Promo code created successfully')
      handleClose()
    },
    onError: () => {
      toast.error('Error creating promo code')
    }
  })

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormSubmit = (values: NewCampaignPromoFormikProps) => {
        const allValues = {
      campaignRewardName: values.campaignRewardName,
      campaignSummary: values.campaignSummary,
      campaignCode: values.campaignCode,
      campaignCodePrefix: values.campaignCodePrefix,
      rewardProductId: values.rewardProductId,
      maxClaims: values.maxClaims,
      maxConsumption: values.maxConsumption,
      validityStartAt: values.validityStartAt,
      validityEndAt: values.validityEndAt,
      faberToken: faberToken
    }
    createPromoCode(allValues)
  }

  /**
   * Event handlers
   */
  const handleUsageTypeSelect = (
    e,
    setFieldValue: (
      field: string,
      value,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setFieldValue(e.target.name, e.target.value)
  }

  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewCampaignPromoSchema}
    >
      {({
        submitForm,
        handleChange,
        errors,
        touched,
        handleBlur,
        setFieldValue
      }) => (
        <Form>
          <Grid container rowSpacing={2}>
            {/* CAMPAIGN PROMO NAME */}
            <Grid item xs={3}>
              <NormalText isBold>Campaign Promo Name</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='campaignRewardName'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'campaign-name'
                  }}
                  fullWidth
                  variant='outlined'
                  error={
                    !!(errors.campaignRewardName && touched.campaignRewardName)
                  }
                  helperText={
                    errors.campaignRewardName && touched.campaignRewardName
                      ? errors.campaignRewardName
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Campaign Summary</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'campaign-summary'
                  }}
                  name='campaignSummary'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.campaignSummary && touched.campaignSummary)}
                  helperText={
                    errors.campaignSummary && touched.campaignSummary
                      ? errors.campaignSummary
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Campaign Code</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'campaign-code'
                  }}
                  name='campaignCode'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.campaignCode && touched.campaignCode)}
                  helperText={
                    errors.campaignCode && touched.campaignCode
                      ? errors.campaignCode
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Campaign Prefix</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'campaign-code-prefix'
                  }}
                  name='campaignCodePrefix'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={
                    !!(errors.campaignCodePrefix && touched.campaignCodePrefix)
                  }
                  helperText={
                    errors.campaignCodePrefix && touched.campaignCodePrefix
                      ? errors.campaignCodePrefix
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Reward Product ID</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                defaultValue=''
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='rewardProductId'
                label=''
              >
                {rewardProducts &&
                  rewardProducts.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.reward_description}
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Max Claims</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  name='maxClaims'
                  type='number'
                  inputProps={{
                    min: 0
                  }}
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  error={!!(errors.maxClaims && touched.maxClaims)}
                  helperText={
                    errors.maxClaims && touched.maxClaims
                      ? errors.maxClaims
                      : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
            <Grid item xs={3}>
              <NormalText isBold>Max Consumption</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  name='maxConsumption'
                  type='number'
                  inputProps={{
                    min: 0
                  }}
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  error={!!(errors.maxConsumption && touched.maxConsumption)}
                  helperText={
                    errors.maxConsumption && touched.maxConsumption
                      ? errors.maxConsumption
                      : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
            <Grid item xs={3}>
              <NormalText isBold>Validity Start Date</NormalText>
            </Grid>
            <Grid item xs={9}>
              <CustomDatePicker
                label='Validity Start Date'
                date={validityStartAt}
                setDate={(value: Date | null) => {
                  setValidityStart(value)
                  // Convert the Date object to Unix time
                  const unixTime = value
                    ? Math.floor(value.getTime())
                    : null
                  setFieldValue('validityStartAt', unixTime)
                }}
                disabled={false}
              />
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Validity End Date</NormalText>
            </Grid>
            <Grid item xs={9}>
              <CustomDatePicker
                label='Validity End Date'
                date={validityEndAt}
                setDate={(value: Date | null) => {
                  setValidityEnd(value)
                  // Convert the Date object to Unix time
                  const unixTime = value
                    ? Math.floor(value.getTime())
                    : null
                  setFieldValue('validityEndAt', unixTime)
                }}
                disabled={false}
              />
            </Grid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button autoFocus onClick={submitForm}>
                Save changes
              </Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
interface NewCampaignPromoFormikProps {
  campaignRewardName: string
  campaignSummary: string
  campaignCode: string
  campaignCodePrefix?: string
  rewardProductId: string
  maxClaims: number
  maxConsumption: number
  validityStartAt: number
  validityEndAt: number
}
